import React, { Fragment, Component } from 'react';
/**
 * CheatSheetDrawer — Bottom sheet drawer for the Spiekbriefje (hint system).
 *
 * Slides up from the bottom with spring animation. Renders SubSkillCheatSheet
 * content with a fallback when no hints are available. Includes focus trap,
 * escape-to-close, and body scroll lock.
 *
 * Co-located in practice/ domain (CL-1 s63).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { type ModuleId, MODULES } from '../../content';
import { SubSkillCheatSheet } from './SubSkillCheatSheet';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
import { useSwipeToDismiss } from '../../app/hooks/useSwipeToDismiss';
import { DjayAvatar, useDjaySelection, getDjayCheatsheetFallback } from '../../mascot';
// ── Internal Sub-components ──────────────────────────────────
function CheatSheetContent({
  moduleId,
  subSkill,
  familyId
}: {
  moduleId: ModuleId;
  subSkill?: string;
  familyId?: string;
}) {
  return <Fragment data-id="element-7885">
      <SubSkillCheatSheet moduleId={moduleId} subSkill={subSkill} familyId={familyId} data-id="element-7886" />
      <CheatSheetFallback data-id="element-7887" />
    </Fragment>;
}
function CheatSheetFallback() {
  const fallback = getDjayCheatsheetFallback();
  return <div className="cheatsheet-fallback text-center py-8 text-gray-400 hidden last:block" data-id="element-7888">
      {/* @djay-slot: cheatsheet-fallback */}
      <div className="flex justify-center mb-3" data-id="element-7889">
        <DjayAvatar size="lg" emotion={fallback.emotion} data-id="element-7890" />
      </div>
      <p className="text-sm font-medium text-gray-500" data-id="element-7891">{fallback.text}</p>
      <p className="text-xs text-gray-400 mt-1" data-id="element-7892">
        Voor deze vaardigheid is nog geen spiekbriefje gemaakt. Probeer de
        hint-knop tijdens het oefenen.
      </p>
    </div>;
}
// ── Types ────────────────────────────────────────────────────
interface CheatSheetDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: ModuleId;
  subSkill?: string;
  familyId?: string;
}
// ── Component ────────────────────────────────────────────────
export function CheatSheetDrawer({
  isOpen,
  onClose,
  moduleId,
  subSkill,
  familyId
}: CheatSheetDrawerProps) {
  const focusTrapRef = useFocusTrap(isOpen);
  const {
    selectedVariant
  } = useDjaySelection();
  useEscapeKey(onClose, isOpen);
  useBodyScrollLock(isOpen);
  const swipe = useSwipeToDismiss({
    onDismiss: onClose,
    disabled: !isOpen
  });
  return <AnimatePresence data-id="element-7893">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-7894" />

          {/* Bottom Sheet */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="cheatsheet-title" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 350
      }} {...swipe.dragProps} className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[75vh] flex flex-col" data-id="element-7895">
            {/* Drag Handle — only this region initiates swipe-to-dismiss
                so the inner scrollable content keeps scrolling on touch. */}
            <div {...swipe.handleProps} onClick={onClose} className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing" role="button" aria-label="Sleep omlaag of tik om te sluiten" data-id="element-7896">
              <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-7897" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100" data-id="element-7898">
              <div className="flex items-center gap-2.5" data-id="element-7899">
                <DjayAvatar size="md" emotion="pointing" variant={selectedVariant} data-id="element-7900" />
                <div data-id="element-7901">
                  <h2 id="cheatsheet-title" className="text-lg font-bold text-slate-900 leading-tight" data-id="element-7902">
                    Spiekbriefje
                  </h2>
                  <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider" data-id="element-7903">
                    {MODULES.find(m => m.id === moduleId)?.title ?? moduleId}
                    {subSkill ? ` · ${subSkill}` : ''}
                  </p>
                </div>
              </div>

              <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600" aria-label="Sluiten" data-id="element-7904">
                <X className="w-5 h-5" data-id="element-7905" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4" data-id="element-7906">
              <CheatSheetContent moduleId={moduleId} subSkill={subSkill} familyId={familyId} data-id="element-7907" />
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}